import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { useCore } from "../lib/core";

/**
 * Route component for `/$tenantSlug/help` — the guide read as a manual.
 *
 * Loads `@praesto/help` on mount rather than at boot: someone who never opens
 * the guide never downloads it.
 *
 * The open article lives in the URL (`?a=<id>`), not in component state, so the
 * address bar always matches what is on screen: a link to an article can be
 * sent to a colleague, and Back walks through the articles you read.
 */
export function HelpPage() {
  const core = useCore();
  const location = useLocation();
  const navigate = useNavigate();
  const [mod, setMod] = useState<typeof import("@praesto/help") | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    void import("@praesto/help")
      .then((m) => {
        if (!cancelled) setMod(m);
      })
      .catch(() => {
        // Without this the page sat on "Caricamento…" forever, with no way back
        // except a reload — the failure mode of a stale index.html pointing at
        // a chunk the last deploy replaced.
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  const articles = useMemo(() => {
    if (!mod) return [];
    return mod.visibleArticles(mod.catalog, {
      isModuleActive: (id) => core.tenant.isModuleActive(id),
      can: (action, resource) => core.rbac.can(action, resource),
    });
  }, [mod, core]);

  // Read from the router's location, not `window.location`: TanStack changes
  // the search without remounting this route.
  const currentId = (location.search as { a?: string } | undefined)?.a;

  const select = useCallback(
    (id: string | null) => {
      void navigate({
        to: ".",
        search: (id ? { a: id } : {}) as never,
        // Replace while browsing the index so Back leaves the guide instead of
        // stepping through every article the reader glanced at.
        replace: !id,
      });
    },
    [navigate],
  );

  if (failed) {
    return (
      <div className="p-6">
        <p className="text-sm text-fg-muted">{core.i18n.t("help.loadFailed")}</p>
        {/* Outline-only control: --border-control, not the card border (WCAG 1.4.11). */}
        <button
          type="button"
          onClick={() => setAttempt((n) => n + 1)}
          className="mt-3 rounded-md border border-border-control px-3 py-1.5 text-sm min-h-touch hover:bg-hover"
        >
          {core.i18n.t("help.retry")}
        </button>
      </div>
    );
  }

  if (!mod) {
    return <p className="p-6 text-sm text-fg-muted">{core.i18n.t("help.loading")}</p>;
  }

  const Book = mod.HelpBook;

  return (
    <Book
      articles={articles}
      t={(key: string) => core.i18n.t(key)}
      currentId={currentId}
      onSelect={select}
    />
  );
}
